import React, { useEffect, useId, useState, ChangeEvent } from "react";
import {
  Button,
  Input,
  Label,
  Select,
  Toaster,
} from "@fluentui/react-components";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import OverlayLoader from "../../../components/OverlayLoader";
import { useToastMessage } from "../../../components/ToastMessage/ToastMessage";
import TextEditor from "../../../components/TextEditor";
import apiServices from "../../../service";
import { getPaymentStatus, stripHtml } from "../../../utils";
import { setDialogModalOptions } from "../../../redux/modelDialogReducer";
import ModalDialog from "../../../components/ModalDialog";
import { fetchDistributionDetails } from "../../../redux/Commission/distributionSlice";

interface SelectOption {
  value: string;
  label: string;
}

interface SelectField {
  field: string;
  options: SelectOption[];
}

const DistributionForm = (props: any) => {
  const selectId = useId();
  const { showToast, toasterId } = useToastMessage();
  const dispatch: AppDispatch = useDispatch();
  const distributionDetails: any = useSelector(
    (state: RootState) => state.distribution.distributionDetails,
  );
  const isFetching: boolean = useSelector(
    (state: RootState) => state.distribution.loading.distributionDetails,
  );
  const dialogModalOptions = useSelector((state: RootState) => state.dialog);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({}); // Validation state
  const [originalField, setOriginalField] = useState<any>(null);
  const [getRuleId, setGetRuleId] = useState<{ [key: string]: any }>({});

  const [formField, setFormField] = useState<any>({
    status: "",
    current_rule: "",
    party_type: "",
    case_type: "",
    party_name: "",
    amount: "0.00",
    commission_id: "",
    commission_rule_id: "",
    commission_allocation_id: "",
    note_text: "",
    amount_flag: 0 || "0",
  });

  function ruleId(id: { [key: string]: any }) {
    setGetRuleId(id);
  }

  useEffect(() => {
    if (distributionDetails && Object.keys(distributionDetails).length > 0) {
      const data = {
        status: getPaymentStatus(distributionDetails.status),
        current_rule: distributionDetails.current_rule || "",
        party_type: distributionDetails.partyType || "",
        case_type: distributionDetails.caseType || props?.data?.caseType || "",
        party_name: distributionDetails.partyName || "",
        amount: distributionDetails.receivedAmount || "0.00",
        commission_id: distributionDetails.commissionId || "",
        commission_rule_id: distributionDetails.commissionRuleId || "",
        commission_allocation_id: distributionDetails.commissionAllocationId || "",
        note_text: distributionDetails.noteText || "",
        amount_flag: distributionDetails.amountFlag,
      };
      setFormField(data);
      setOriginalField(data);
    }
  }, [distributionDetails]);

  // Convert status to number for easier logic
  const status = Number(distributionDetails?.status);
  const isEditable = status === 0 || status === 1;

  useEffect(() => {
    if (!props?.data?.commissionAllocationId) return;
    dispatch(fetchDistributionDetails(props.data.commissionAllocationId));
  }, [props?.data?.commissionAllocationId, dispatch]);

  // console.log("props.data", props.data)

  useEffect(() => {
    if (getRuleId && Object.keys(getRuleId).length > 0) {
      setFormField((prev: any) => ({
        ...prev,
        case_type: getRuleId.case_type || prev.case_type,
        party_type: getRuleId.party_type || prev.party_type,
        party_name: getRuleId.party_name || prev.party_name,
        note_text: getRuleId.note_text || prev.note_text,
        amount: getRuleId.amount || prev.amount,
        commission_rule_id: getRuleId.rule_id || prev.commission_rule_id,
        commission_allocation_id: getRuleId.id || prev.commission_allocation_id,
        amount_flag: getRuleId.amount_flag || prev.amount_flag,
      }));
    }
  }, [getRuleId]);

  useEffect(() => {
    if (distributionDetails && !originalField) {
      const initialValues = {
        party_type: distributionDetails?.partyType || "",
        party_name: distributionDetails?.partyName || "",
        amount: distributionDetails?.receiptAmount || "0.00",
        commission_rule_id: distributionDetails?.commissionRuleId || "",
        note_text: distributionDetails?.note || "",
      };
      setOriginalField(initialValues);
    }
  }, [distributionDetails]);

  const hasChanges = () => {
    if (!originalField) return false;
    const currentNote = stripHtml(formField.note_text).trim();
    const originalNote = stripHtml(originalField.note_text).trim();

    return (
      formField.party_type !== originalField.party_type ||
      formField.party_name !== originalField.party_name ||
      formField.amount !== originalField.amount ||
      formField.commission_rule_id !== originalField.commission_rule_id ||
      currentNote !== originalNote
    );
  };
  // Always initialize as array
  const [selectFieldOptions, setSelectFieldOptions] = useState<SelectField[]>(
    []
  );

  /** Fetch list options for dropdowns */
  const getSelectionListOptions = async () => {
    try {
      const fieldLbl =
        "commission_party_type:FixedList,party_name:MasterDatawithNewEntry,true_false:FixedList";
      const res = await apiServices.selectionList.getOptions(fieldLbl);

      if (res.data && res.data.data) {
        const responseData = res.data.data;

        // Normalize response: always return array of { field, options }
        let formattedOptions: SelectField[] = [];

        if (Array.isArray(responseData)) {
          formattedOptions = responseData;
        } else if (typeof responseData === "object") {
          formattedOptions = Object.keys(responseData).map((key) => ({
            field: key,
            options: responseData[key],
          }));
        }

        setSelectFieldOptions(formattedOptions);
      } else {
        setSelectFieldOptions([]);
      }
    } catch (error) {
      console.error("Error fetching selection options:", error);
      showToast("Failed to load dropdown options", "", "error");
      setSelectFieldOptions([]);
    }
  };

  useEffect(() => {
    getSelectionListOptions();
  }, []);

  /** Handle input change */
  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormField((prev: any) => ({ ...prev, [name]: value }));
  };

  /** Handle submit */
  const handleUpdateDistribution = async () => {
    // Validation Check
    const newErrors = {
      party_type: !formField.party_type,
      party_name: !formField.party_name,
    };
    const isPartyTypeMissing = !formField.party_type;
    const isPartyNameMissing = !formField.party_name;
    setErrors({
      party_type: isPartyTypeMissing,
      party_name: isPartyNameMissing,
    });

    if (isPartyTypeMissing || isPartyNameMissing) {
      if (isPartyTypeMissing && isPartyNameMissing) {
        showToast("Please select Party Type and Party Name", "", "error");
      } else if (isPartyTypeMissing) {
        showToast("Please select Party Type", "", "error");
      } else if (isPartyNameMissing) {
        showToast("Please select Party Name", "", "error");
      }
      return;
    }

    setLoading(true);
    try {
      const sanitizedNote = formField.note_text ? stripHtml(formField.note_text).trim() : "";
      const updateValue = {
        commission_rule_id: formField.commission_rule_id,
        party_type: formField.party_type,
        party_name: formField.party_name,
        amount: formField.amount,
        note: sanitizedNote,
      };

      const response = await apiServices.distribution.update(
        formField.commission_allocation_id,
        updateValue
      );

      if (!response.data.isError || response.data?.message !== "Commission not found") {
        showToast("Distribution updated successfully", "", "success");
        setTimeout(() => props.setReceiptEditpopup(false), 2000);
      } else {
        showToast(response?.data?.error?.message || response.data?.message || "Update failed", "", "error");
      }
    } catch (error) {
      showToast("Failed to update distribution", "", "error");
    } finally {
      setLoading(false);
    }
  };

  const isGlobalLoading = loading || isFetching;

  // Helper to get options by field
  const getOptionsByField = (fieldName: string): SelectOption[] => {
    const field = selectFieldOptions.find((opt) => opt.field === fieldName);
    return field?.options || [];
  };

  return (
    <div>
      <OverlayLoader isLoading={loading} />
      <ModalDialog options={dialogModalOptions} />
      <Toaster
        toasterId={toasterId}
        position="top-end"
        pauseOnHover
        pauseOnWindowBlur
        timeout={1000}
      />

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          appearance="secondary"
          size="medium"
          disabled={!isEditable}
          onClick={() => {
            dispatch(
              setDialogModalOptions({
                open: true,
                rule: true,
                ruleId: ruleId,
                getRuleId: getRuleId,
                cancel: {
                  onclick: () => {
                    dispatch(setDialogModalOptions({ open: false }));
                  },
                  label: "Cancel",
                },
                no: {
                  onclick: () => {
                    dispatch(setDialogModalOptions({ open: false }));
                  },
                  label: "Select",
                  type: "primary",
                },
              })
            );
          }}
        >
          Choose Distribution Rule
        </Button>
      </div>

      <div className="commission_action_main">
        {/* Status */}
        <Label className="settings-table-content-label">Status</Label>
        <Input name="status" value={formField.status} readOnly disabled />

        {/* Current Rule */}
        <Label className="settings-table-content-label">Current Rule</Label>
        <Input
          name="current_rule"
          value={formField.current_rule}
          readOnly
          disabled
        />

        {/* Party Type */}
        <Label required className="settings-table-content-label">
          Party Type
        </Label>
        <Select
          id={`${selectId}-party-type`}
          disabled={!isEditable || isGlobalLoading}
          name="party_type"
          value={formField.party_type}
          onChange={(e) => {
            setFormField({ ...formField, party_type: e.target.value });
            if (e.target.value) setErrors(prev => ({ ...prev, party_type: false }));
          }}
          style={{ borderColor: errors.party_type ? "red" : "" }}
        >
          <option value="">Select...</option>
          {getOptionsByField("commission_party_type").map((opt, index) => (
            <option key={index} value={opt.value}>{opt.label}</option>
          ))}
        </Select>

        {/* Case Type */}
        <Label className="settings-table-content-label">Case Type</Label>
        <Input name="case_type" value={formField.case_type} readOnly disabled />

        {/* Party Name */}
        <Label required className="settings-table-content-label">
          Party Name
        </Label>
        <Select
          id={`${selectId}-party-name`}
          name="party_name"
          value={formField.party_name}
          disabled={!isEditable || isGlobalLoading}
          onChange={(e) => {
            setFormField({ ...formField, party_name: e.target.value });
            if (e.target.value) setErrors(prev => ({ ...prev, party_name: false }));
          }}
          style={{ borderColor: errors.party_name ? "red" : "" }}
        >
          <option value="">Select...</option>
          {getOptionsByField("party_name").map((opt, index) => (
            <option key={index} value={opt.value}>{opt.label}</option>
          ))}
        </Select>

        {/* Amount */}
        <Label className="settings-table-content-label">Amount</Label>
        <Input
          type="number"
          name="amount"
          value={formField.amount}
          readOnly
          onChange={onChange}
          disabled={!isEditable || isGlobalLoading}
          contentAfter={
            formField?.amount_flag === 1 ||
              formField?.amount_flag === "1" ? (
              <strong>%</strong>
            ) : null
          }
        />

        {/* Rule ID */}
        <Label className="settings-table-content-label">Rule ID</Label>
        <Input
          name="commission_rule_id"
          value={formField.commission_rule_id}
          readOnly
          disabled
        />

        {/* Note */}
        <Label className="settings-table-content-label">Note</Label>
        <TextEditor
          inputData={formField.note_text}
          disabled={!isEditable || isGlobalLoading}
          onChange={(text: string) => {
            const cleanedText = stripHtml(text).trim();
            const sanitizedText =
              /^(<p>\s*<br>\s*<\/p>|\s*)$/i.test(text.trim()) ||
                cleanedText === ""
                ? ""
                : text;
            setFormField((prev: any) => ({ ...prev, note_text: sanitizedText }));
          }}
        />
      </div>

      <div className="button-end-popup">
        {(status === 0 || status === 1) && (
          <Button
            shape="rounded"
            onClick={() => props?.setStopDistributionConfirmationPopup(true)}
            style={{ marginRight: 6 }}
          >
            Stop Distribution
          </Button>
        )}
        <Button
          shape="rounded"
          appearance="secondary"
          size="medium"
          style={{ marginRight: 6 }}
          onClick={handleUpdateDistribution}
          disabled={loading || !isEditable || !hasChanges()}
        >
          Save
        </Button>

        <Button
          shape="rounded"
          appearance="primary"
          className="asc-button-primary"
          size="medium"
          onClick={() => props.setReceiptEditpopup(false)}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default DistributionForm;
